/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * OIDC pending-signin-state repair for the native (Capacitor) sign-in path.
 *
 * Root cause this module addresses
 * --------------------------------
 * `oidc-client-ts`' `UserManager.signinCallback(url)` is a *dispatcher*: it reads
 * the stored signin state keyed by the `state` URL parameter and switches on the
 * state's internal `request_type` field (`"si:r"` redirect, `"si:p"` popup,
 * `"si:s"` silent). When `request_type` is missing or is none of those values it
 * falls through to `default` and throws the (misleadingly named) error
 * **"invalid response_type in state"** — the word is `response_type` but the
 * switch is on `request_type`, and it is unrelated to the OAuth `response_type=code`.
 *
 * On the web the stored state always carries `request_type: "si:r"` because the
 * app starts login with `UserManager.signinRedirect()`, which stamps it. On the
 * native shell the authorize step can round-trip through an external browser /
 * deeplink hop, and the pending-state entry that `signinCallback()` resolves can
 * end up WITHOUT a valid `request_type` — producing exactly the native trace
 * `signin_callback_fail {"message":"invalid response_type in state"}` → bounce.
 *
 * The repair
 * ----------
 * This app only ever uses the authorization-code **redirect** flow
 * (`response_type: "code"`, started via `signinRedirect`). So a pending state
 * whose `request_type` is absent/invalid can only legitimately be a redirect
 * request. We re-stamp it to `"si:r"` BEFORE the callback reads it. This makes
 * `signinCallback()` dispatch to `signinRedirectCallback()`, which still performs
 * the full, meaningful validation — PKCE `code_verifier` → token exchange, `state`
 * match, and `nonce` check. No security check is weakened: we only repair the
 * internal routing tag, never the OAuth response validation.
 */

/** The redirect request_type oidc-client-ts stores for `signinRedirect`. */
export const REDIRECT_REQUEST_TYPE = 'si:r';

/** Request types oidc-client-ts' signinCallback dispatcher accepts. */
const VALID_REQUEST_TYPES = new Set(['si:r', 'si:p', 'si:s']);

/** localStorage key prefix oidc-client-ts uses for pending signin state. */
const OIDC_STATE_PREFIX = 'oidc:state';

/**
 * Extracts the `state` id from an OIDC callback URL's query or fragment.
 *
 * @param callbackUrl The full callback URL (deeplink or in-WebView redirect).
 * @returns The `state` parameter value, or null when absent/unparseable.
 */
export function readStateIdFromCallbackUrl(callbackUrl: string): string | null {
  try {
    const url = new URL(callbackUrl);
    const fromQuery = url.searchParams.get('state');
    if (fromQuery) return fromQuery;
    // Some providers return params in the fragment.
    const fragment = url.hash.startsWith('#') ? url.hash.slice(1) : url.hash;
    const fromFragment = new URLSearchParams(fragment).get('state');
    return fromFragment || null;
  } catch {
    return null;
  }
}

/**
 * Decides whether a stored signin-state's `request_type` would make
 * `signinCallback()` throw "invalid response_type in state".
 *
 * @param requestType The `request_type` field read from the stored state.
 * @returns True when the value is missing or not an accepted request type.
 */
export function isUnroutableRequestType(requestType: unknown): boolean {
  return typeof requestType !== 'string' || !VALID_REQUEST_TYPES.has(requestType);
}

/**
 * Repairs a single stored OIDC signin-state JSON string: if its `request_type`
 * is missing or invalid, re-stamp it to the redirect type `"si:r"`.
 *
 * @param stateJson The raw JSON string stored under an `oidc:state` key.
 * @returns The repaired JSON string, or the original when no repair was needed
 *          or the input was not parseable signin-state JSON.
 */
export function repairSigninStateJson(stateJson: string): string {
  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(stateJson) as Record<string, unknown>;
  } catch {
    return stateJson;
  }
  if (!parsed || typeof parsed !== 'object') return stateJson;
  if (!isUnroutableRequestType(parsed.request_type)) return stateJson;
  parsed.request_type = REDIRECT_REQUEST_TYPE;
  return JSON.stringify(parsed);
}

/**
 * Before processing an OIDC callback, ensure the pending signin-state that the
 * callback will read carries a routable `request_type`. This prevents the native
 * sign-in callback from throwing "invalid response_type in state" and bouncing
 * the user back to the landing page.
 *
 * It targets the specific pending state identified by the callback URL's `state`
 * id (never touching unrelated entries). When that state id cannot be read, it
 * falls back to repairing any `oidc:state*` entry in storage — safe because the
 * app only uses the redirect flow.
 *
 * @param callbackUrl The OIDC callback URL about to be processed.
 * @param storage The storage holding the pending state (OIDC `stateStore`).
 * @returns True when a repair was applied, false otherwise.
 */
/**
 * Repairs a single stored entry in place, writing back only when changed.
 *
 * @param storage The storage holding the pending state.
 * @param key The storage key to repair.
 * @returns True when a repair was written, false otherwise.
 */
function repairStorageEntry(storage: Storage, key: string): boolean {
  const raw = storage.getItem(key);
  if (raw === null) return false;
  const repaired = repairSigninStateJson(raw);
  if (repaired === raw) return false;
  storage.setItem(key, repaired);
  return true;
}

/**
 * Repairs every pending `oidc:state*` entry in storage.
 *
 * @param storage The storage holding the pending state entries.
 * @returns True when at least one entry was repaired.
 */
function repairAllPendingStates(storage: Storage): boolean {
  let repairedAny = false;
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (!key || !key.startsWith(OIDC_STATE_PREFIX)) continue;
    if (repairStorageEntry(storage, key)) repairedAny = true;
  }
  return repairedAny;
}

/**
 * Before processing an OIDC callback, ensure the pending signin-state that the
 * callback will read carries a routable `request_type`. This prevents the native
 * sign-in callback from throwing "invalid response_type in state" and bouncing
 * the user back to the landing page.
 *
 * It targets the specific pending state identified by the callback URL's `state`
 * id (never touching unrelated entries). When that state id cannot be read, it
 * falls back to repairing any `oidc:state*` entry in storage — safe because the
 * app only uses the redirect flow.
 *
 * @param callbackUrl The OIDC callback URL about to be processed.
 * @param storage The storage holding the pending state (OIDC `stateStore`).
 * @returns True when a repair was applied, false otherwise.
 */
export function ensureRoutableSigninState(
  callbackUrl: string,
  storage: Storage,
): boolean {
  const stateId = readStateIdFromCallbackUrl(callbackUrl);
  if (stateId) {
    return repairStorageEntry(storage, `${OIDC_STATE_PREFIX}${stateId}`);
  }
  // No state id in the URL: repair every pending oidc:state entry.
  return repairAllPendingStates(storage);
}
